import { makeAutoObservable, runInAction } from 'mobx'
import {
  getFirestore,
  doc,
  updateDoc,
  serverTimestamp,
  collection,
  setDoc,
  deleteDoc,
  writeBatch,
  getDoc,
  deleteField,
} from 'firebase/firestore'
import FirebaseApp from '../../../shared/api/firebase'
import { eachDayOfInterval } from 'date-fns'

const db = getFirestore(FirebaseApp)

class WorkingHoursStore {
  isSubmitting = false // Локальное состояние загрузки для операций с графиком
  isSettingsSubmitting = false // Для настроек буферного времени и праздников
  staffWorkingHours = {}
  isWorkingHoursLoading = false

  constructor(rootStore) {
    makeAutoObservable(this, { rootStore: false }, { autoBind: true })
    this.rootStore = rootStore
    this.organizationStore = rootStore.organizationStore
    this.errorStore = rootStore.errorStore
    this.staffStore = rootStore.staffStore
  }
  // Метод был упрощен, так как параметры startDate и endDate и сложный запрос не использовались.
  // Теперь он просто загружает основной график сотрудника.
  async fetchStaffWorkingHours(staffId) {
    console.log('staffId', staffId)
    console.log('this.staffWorkingHours', this.staffWorkingHours)
    const orgId = this.organizationStore.organization?.id
    if (!orgId || !staffId) return

    // Не перезагружать, если данные уже есть (более надежная проверка)
    if (Object.prototype.hasOwnProperty.call(this.staffWorkingHours, staffId)) {
      return
    }

    this.isWorkingHoursLoading = true
    try {
      const staffDocRef = doc(db, 'organizations', orgId, 'staff', staffId)
      const docSnap = await getDoc(staffDocRef)
      let schedule = null

      if (docSnap.exists()) {
        const data = docSnap.data()
        // Сохраняем весь график (и дни, и часы)
        // The new data model stores all schedule info in `workDays`.
        // The top-level `workHours` is deprecated and no longer used.
        schedule = {
          workDays: data.workDays,
        }
      } else {
        console.warn(`Документ сотрудника с ID ${staffId} не найден.`)
        schedule = {} // Сохраняем пустой объект, чтобы не перезапрашивать
      }
      runInAction(() => {
        this.staffWorkingHours[staffId] = schedule
      })
    } catch (error) {
      console.error('Ошибка при загрузке рабочих часов сотрудника:', error)
      this.errorStore.showError('Не удалось загрузить рабочие часы')
    } finally {
      runInAction(() => {
        this.isWorkingHoursLoading = false
      })
    }
  }
  // Обновляет настройки буферного времени
  async updateBufferTime(bufferTime) {
    this.isSettingsSubmitting = true
    try {
      // Вызываем метод в OrganizationStore, так как это настройка всей организации.
      // Передаем `false`, чтобы не показывать общее сообщение "Профиль сохранен".
      await this.organizationStore.updateOrganization({ bufferTime }, false)
      runInAction(() => {
        this.errorStore.showSuccess('Буферное время обновлено')
      })
    } catch (error) {
      console.error('Ошибка при обновлении буферного времени:', error)
      this.errorStore.showError('Не удалось обновить буферное время')
    } finally {
      runInAction(() => {
        this.isSettingsSubmitting = false
      })
    }
  }

  // Обновляет настройки праздников
  async updateHolidaySettings(settings) {
    this.isSettingsSubmitting = true
    try {
      await this.organizationStore.updateOrganization(settings, false)
      runInAction(() => {
        this.errorStore.showSuccess('Настройки праздников обновлены')
      })
    } catch (error) {
      console.error('Ошибка при обновлении настроек праздников:', error)
      this.errorStore.showError('Не удалось обновить настройки праздников')
    } finally {
      runInAction(() => {
        this.isSettingsSubmitting = false
      })
    }
  }

  // Обновляет основной график для конкретного сотрудника
  async updateStaffSchedule(staffId, newSchedule) {
    const orgId = this.organizationStore.organization.id
    if (!orgId || !staffId || !newSchedule) return

    this.isSubmitting = true
    try {
      const staffDocRef = doc(db, 'organizations', orgId, 'staff', staffId)
      await updateDoc(staffDocRef, {
        workDays: newSchedule.workDays, // The entire schedule is now in workDays
        workHours: deleteField(), // Remove the deprecated top-level field
        updatedAt: serverTimestamp(),
      })

      // Оптимистичное обновление локального состояния в OrganizationStore
      runInAction(() => {
        const staffMember = this.staffStore.staff.find((s) => s.id === staffId)
        if (staffMember) {
          staffMember.workDays = newSchedule.workDays
          delete staffMember.workHours // Also remove from local state
        }
        this.errorStore.showSuccess('График работы обновлен')
      })
      return true
    } catch (error) {
      console.error('Ошибка при обновлении графика:', error)
      this.errorStore.showError('Не удалось обновить график работы')
      return false
    } finally {
      runInAction(() => {
        this.isSubmitting = false
      })
    }
  }

  // Добавляет исключение для сотрудника
  async addStaffException(staffId, exceptionData) {
    const orgId = this.organizationStore.organization.id
    if (!orgId || !staffId || !exceptionData?.date) return

    this.isSubmitting = true
    try {
      const exceptionCollectionRef = collection(
        db,
        'organizations',
        orgId,
        'staff',
        staffId,
        'exceptions',
      )
      const newDocRef = doc(exceptionCollectionRef)
      const newExceptionData = {
        ...exceptionData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }
      await setDoc(newDocRef, newExceptionData)

      // Оптимистичное обновление
      runInAction(() => {
        const staffMember = this.staffStore.staff.find((s) => s.id === staffId)
        if (staffMember) {
          const newExceptionForState = {
            ...exceptionData, // exceptionData.date is a JS Date
            id: newDocRef.id,
            // Имитируем объект Timestamp для консистентности с данными из Firestore,
            // чтобы UI (который вызывает .toDate()) не ломался
            date: { toDate: () => exceptionData.date },
            createdAt: new Date(),
            updatedAt: new Date(),
          }
          const existingExceptions = staffMember.exceptions || []
          staffMember.exceptions = [...existingExceptions, newExceptionForState]
        }
        this.errorStore.showSuccess('Исключение добавлено')
      })
    } catch (error) {
      console.error('Ошибка при добавлении исключения:', error)
      this.errorStore.showError('Не удалось добавить исключение')
    } finally {
      runInAction(() => {
        this.isSubmitting = false
      })
    }
  }

  // Добавляет диапазон исключений (например, отпуск)
  async addStaffExceptionRange(staffId, rangeData) {
    const orgId = this.organizationStore.organization.id
    const { startDate, endDate, ...baseExceptionData } = rangeData

    if (!orgId || !staffId || !startDate || !endDate) return

    this.isSubmitting = true
    try {
      const batch = writeBatch(db)
      const exceptionCollectionRef = collection(
        db,
        'organizations',
        orgId,
        'staff',
        staffId,
        'exceptions',
      )

      const days = eachDayOfInterval({ start: startDate, end: endDate })
      const newExceptionsForState = []

      days.forEach((day) => {
        const newDocRef = doc(exceptionCollectionRef)
        const newExceptionData = {
          ...baseExceptionData,
          date: day,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        }
        batch.set(newDocRef, newExceptionData)

        // Для оптимистичного обновления
        newExceptionsForState.push({
          ...baseExceptionData,
          id: newDocRef.id,
          date: { toDate: () => day }, // Совместимость с UI
        })
      })

      await batch.commit()

      runInAction(() => {
        const staffMember = this.staffStore.staff.find((s) => s.id === staffId)
        if (staffMember) {
          const existingExceptions = staffMember.exceptions || []
          staffMember.exceptions = [
            ...existingExceptions,
            ...newExceptionsForState,
          ]
        }
        this.errorStore.showSuccess('Период отпуска добавлен')
      })
    } catch (error) {
      console.error('Ошибка при добавлении периода исключений:', error)
      this.errorStore.showError('Не удалось добавить период исключений')
    } finally {
      runInAction(() => {
        this.isSubmitting = false
      })
    }
  }

  // Удаляет исключение у сотрудника
  async removeStaffException(staffId, exceptionId) {
    const orgId = this.organizationStore.organization.id
    if (!orgId || !staffId || !exceptionId) return

    try {
      const docRef = doc(
        db,
        'organizations',
        orgId,
        'staff',
        staffId,
        'exceptions',
        exceptionId,
      )
      await deleteDoc(docRef)

      runInAction(() => {
        const staffMember = this.staffStore.staff.find((s) => s.id === staffId)
        if (staffMember && staffMember.exceptions) {
          staffMember.exceptions = staffMember.exceptions.filter(
            (ex) => ex.id !== exceptionId,
          )
        }
        this.errorStore.showSuccess('Исключение удалено')
      })
    } catch (error) {
      console.error('Ошибка при удалении исключения:', error)
      this.errorStore.showError('Не удалось удалить исключение')
    }
  }
}

export default WorkingHoursStore
